import {
  buildAnnouncementKey,
  markReminderDismissed,
  REMINDER_LEAD_MINUTES,
} from './index'
import { formatTimeRangeWithTimezone } from '@thyme/libs/src/date/formatting'
import { minutesToMs } from '@thyme/libs/src/time/convert'
import { AllowedIcons } from '@thyme/nashville/src/types/icons'
import { MessageSeverity } from '@thyme/nashville/src/types/messages'
import { DateTime } from 'luxon'
import { dateTimeToString } from '@/libs/date/date'
import type {
  DateOnlyUpcomingCall,
  TimedUpcomingCall,
  UpcomingCall,
} from './types'
import type { TToastMessage } from '@thyme/nashville/src/components/toast/types'
import type { useTToast } from '@thyme/nashville/src/components/toast/useTToast'

const UNKNOWN_MEMBER_NAME = 'Unknown Member'

/**
 * CLEANUP(MET-3015): endDatetime is becoming required on planned calls that have a
 * start time, and the backfill covers historical rows. Once that lands, delete this
 * and read `call.endTime` directly.
 */
export const FALLBACK_CALL_DURATION_MINUTES = 120

export const MINIMUM_TOAST_LIFE_MINUTES = 2

/**
 * When a call ends, assuming a duration for one with no end time on record.
 * @param call
 */
export function effectiveCallEndTime(call: TimedUpcomingCall): DateTime {
  return (
    call.endTime ??
    call.startTime.plus({ minutes: FALLBACK_CALL_DURATION_MINUTES })
  )
}

/**
 * @param call
 */
function timedCallLifeMs(call: TimedUpcomingCall): number {
  // Floored because `end == start` is possible and PV reads a life of 0 as falsy
  // which could accidentally make a toast sticky (tiny edge case)
  return Math.max(
    effectiveCallEndTime(call).diff(DateTime.now()).toMillis(),
    minutesToMs(MINIMUM_TOAST_LIFE_MINUTES)
  )
}

/**
 * @param call
 */
function sharedToastFields(call: UpcomingCall) {
  return {
    id: buildAnnouncementKey(call),
    summary: `Scheduled call with ${call.patientName || UNKNOWN_MEMBER_NAME}`,
    actionLabel: 'Open member profile',
    actionTo: {
      name: '#patientProfile',
      params: { patientId: call.patientId },
      query: { commId: call.communicationId },
    },
    shouldCloseOnAction: true,
  }
}

/**
 * @param call
 * @returns The message to pass to `useTToast().add()`.
 */
export function toTimedCallToastMessage(
  call: TimedUpcomingCall
): TToastMessage {
  return {
    ...sharedToastFields(call),
    headerTitle: `Starts in ${REMINDER_LEAD_MINUTES} minutes`,
    detail: formatTimeRangeWithTimezone(
      dateTimeToString(call.startTime.toUTC()),
      call.endTime ? dateTimeToString(call.endTime.toUTC()) : undefined
    ),
    life: timedCallLifeMs(call),
  }
}

/**
 * @param call
 * @returns The message to pass to `useTToast().add()`.
 */
export function toDateOnlyCallToastMessage(
  call: DateOnlyUpcomingCall
): TToastMessage {
  const now = DateTime.now()
  return {
    ...sharedToastFields(call),
    headerTitle: 'For today',
    detail: 'No call time',
    life: now.endOf('day').diff(now).toMillis(),
  }
}

/**
 * The call that wins the single slot: whichever starts soonest, preference for timed calls vs all day
 * @param calls - The batch handed to the announcer.
 */
function mostUrgentCall(calls: UpcomingCall[]): UpcomingCall | undefined {
  const timed = calls.filter(
    (call): call is TimedUpcomingCall => !call.isDateOnly
  )
  if (!timed.length) return calls.at(0)

  return timed.reduce((soonest, call) =>
    call.startTime.toMillis() < soonest.startTime.toMillis() ? call : soonest
  )
}

/**
 * Timed calls only
 * @param call
 * @returns The message to pass to `useTToast().add()`.
 */
export function toMissedCallToastMessage(
  call: TimedUpcomingCall
): TToastMessage {
  const now = DateTime.now()
  return {
    ...sharedToastFields(call),
    id: buildAnnouncementKey(call, 'missed'),
    severity: MessageSeverity.ERROR,
    headerIcon: AllowedIcons.phoneMissed,
    headerTitle: 'Attention needed',
    summary: `Missed scheduled call with ${call.patientName || UNKNOWN_MEMBER_NAME}`,
    detail: formatTimeRangeWithTimezone(
      dateTimeToString(call.startTime.toUTC()),
      call.endTime ? dateTimeToString(call.endTime.toUTC()) : undefined
    ),
    actionLabel: 'Reschedule call',
    actionTo: {
      name: '#patientProfile',
      params: { patientId: call.patientId },
      query: { commId: call.communicationId, editing: 'true' },
    },
    life: now.endOf('day').diff(now).toMillis(),
  }
}

/**
 * @param toast
 */
export function createCallToastAnnouncer(toast: ReturnType<typeof useTToast>) {
  let lastAnnouncedId: string | null = null

  /**
   * Puts a card in the slot, taking down whatever was there.
   * @param message
   */
  function showInSlot(message: TToastMessage): void {
    if (lastAnnouncedId) {
      toast.remove({ id: lastAnnouncedId })
      markReminderDismissed(lastAnnouncedId)
    }

    lastAnnouncedId = message.id as string
    toast.add(message)
  }

  return {
    /**
     * @param calls
     */
    announceUpcoming(calls: UpcomingCall[]): void {
      const call = mostUrgentCall(calls)
      if (!call) return

      for (const other of calls) {
        if (other === call) continue
        markReminderDismissed(buildAnnouncementKey(other))
      }

      showInSlot(
        call.isDateOnly
          ? toDateOnlyCallToastMessage(call)
          : toTimedCallToastMessage(call)
      )
    },

    /**
     * @param calls
     */
    announceMissed(calls: TimedUpcomingCall[]): void {
      const [call, ...rest] = calls
      if (!call) return

      for (const other of rest) {
        markReminderDismissed(buildAnnouncementKey(other, 'missed'))
      }

      showInSlot(toMissedCallToastMessage(call))
    },
  }
}

/**
 * Remembers user-dismissed toast (not auto-dismissed) so will not re-announce this session
 * Ignores PV-generated toast ids, only tracks our scheduled call string communication:datetime ids
 * @param message - The message the user dismissed.
 */
export function dismissCallReminder(message: TToastMessage): void {
  if (typeof message.id !== 'string') {
    return
  }
  markReminderDismissed(message.id)
}
